import { FC, useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import _debounce from 'lodash/debounce';
import { createPortal } from 'react-dom';

import { SortingDirection, TextHeader, useToast, useDisclosure } from '@chakra-ui-kraud/react';
import { Button } from '@chakra-ui-kraud/react';
import { ChevronRightIcon } from '@chakra-ui/icons';

import { RubberProductTable } from '@/widgets/rubber-product-table';
import { Layout } from '@/widgets';
import { Search } from '@/shared/components/search';
import { UserRoles } from '@/shared/core';
import { useAppSelector } from '@/shared/state';
import { DeleteModal } from '@/shared';
import { RubberModal, RubberModalRef } from '@/features/rubber-form/ui/rubber-modal';
import { showSuccessToast } from '@/shared/components/toasts';

import styles from './rubber-catalog.module.scss';
import {
	RubberCatalogSortingFields,
	useGetRubbersQuery,
	RubberCatalogResponse,
	useDeleteRubberMutation,
} from '@/shared/state/api/swagger';
import { MainContentLoader, showErrorToast } from '@/shared';

export const RubberCatalogPage: FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [searchValue, setSearchValue] = useState(searchParams.get('stringToSearch') || '');
	const userRole = useAppSelector((state) => state.auth.userProfile?.role);
	const toast = useToast();
	const rubberProductModalRef = useRef<RubberModalRef>(null);

	const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
	const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } = useDisclosure();
	const {
		isOpen: isCreateSimilarModalOpen,
		onOpen: onCreateSimilarModalOpen,
		onClose: onCreateSimilarModalClose,
	} = useDisclosure();
	const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState<RubberCatalogResponse | null>(null);

	const handleSearchParamsUpdate = useCallback(
		(value: string) => {
			setSearchParams((prev) => {
				const newParams = new URLSearchParams(prev);
				if (value.trim()) {
					newParams.set('stringToSearch', value.trim());
				} else {
					newParams.delete('stringToSearch');
				}
				newParams.set('currentPage', '1');
				return newParams;
			});
		},
		[setSearchParams],
	);

	const debouncedSearchParamsUpdate = useCallback(_debounce(handleSearchParamsUpdate, 700), [
		handleSearchParamsUpdate,
	]);

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchValue(value);
		debouncedSearchParamsUpdate(value);
	};

	const { data, isLoading, isFetching, isError } = useGetRubbersQuery({
		stringToSearch: searchValue,
		pageSize: Number(searchParams.get('pageSize')) || 32,
		currentPage: Number(searchParams.get('currentPage')) || 1,
		orderBy: (searchParams.get('orderBy') as RubberCatalogSortingFields) || undefined,
		orderDir: (searchParams.get('orderDir') as SortingDirection) || undefined,
	});

	// Modal handlers
	const handleEdit = (product: RubberCatalogResponse) => {
		setSelectedProduct(product);
		onEditModalOpen();
	};

	const handleCreateSimilar = (product: RubberCatalogResponse) => {
		setSelectedProduct(product);
		onCreateSimilarModalOpen();
	};

	const handleDelete = (product: RubberCatalogResponse) => {
		setSelectedProduct(product);
		setDeleteModalOpen(true);
	};

	const handleCreateNew = () => {
		setSelectedProduct(null);
		onCreateModalOpen();
	};

	const [deleteRubber, { isLoading: isDeleteRubberLoading }] = useDeleteRubberMutation();

	const confirmDelete = () => {
		deleteRubber({ rubberId: selectedProduct?.id ?? 0 })
			.unwrap()
			.then(() => {
				showSuccessToast(toast, { description: 'РТИ удалена' });
			})
			.catch(() => {
				showErrorToast(toast, { description: 'При удалении РТИ произошла ошибка' });
			});
		setDeleteModalOpen(false);
	};

	const handleCloseEditModal = () => {
		onEditModalClose();
		setSelectedProduct(null);
	};

	const handleCloseCreateModal = () => {
		onCreateModalClose();
		setSelectedProduct(null);
	};

	const handleCloseCreateSimilarModal = () => {
		onCreateSimilarModalClose();
		setSelectedProduct(null);
	};

	const handleCloseDeleteModal = () => {
		setDeleteModalOpen(false);
		setSelectedProduct(null);
	};

	useEffect(() => {
		if (isError) {
			showErrorToast(toast, {
				description: 'При получении справочника РТИ произошла ошибка',
			});
		}
	}, [isError]);

	return (
		<Layout
			title={
				<TextHeader className={styles['rubber-product-catalog__title']} variant="h1">
					Справочник РТИ
				</TextHeader>
			}
			search={<Search handleChange={handleSearch} value={searchValue} />}
			headerLeftButton={
				userRole !== UserRoles.user && (
					<Button
						size="md"
						variant="ghost"
						colorScheme="primary"
						lineHeight="24px"
						rightIcon={<ChevronRightIcon />}
						onClick={handleCreateNew}
					>
						Добавить изделие
					</Button>
				)
			}
		>
			{isDeleteRubberLoading || (isFetching && searchValue && !isLoading) ? <MainContentLoader /> : <></>}
			<RubberProductTable
				rubbers={data?.payload || []}
				onEdit={handleEdit}
				onCreateSimilar={handleCreateSimilar}
				onDelete={handleDelete}
			/>
			{isEditModalOpen && selectedProduct && (
				<RubberModal
					ref={rubberProductModalRef}
					isOpen={isEditModalOpen}
					onClose={handleCloseEditModal}
					title="Редактирование резиновой детали"
					variant="edit"
					selectedProduct={selectedProduct}
				/>
			)}
			{isCreateModalOpen && (
				<RubberModal
					ref={rubberProductModalRef}
					isOpen={isCreateModalOpen}
					onClose={handleCloseCreateModal}
					title="Создание резиновой детали"
					variant="create"
					selectedProduct={undefined}
				/>
			)}
			{isCreateSimilarModalOpen && selectedProduct && (
				<RubberModal
					ref={rubberProductModalRef}
					isOpen={isCreateSimilarModalOpen}
					onClose={handleCloseCreateSimilarModal}
					title="Создание похожей резиновой детали"
					variant="create"
					selectedProduct={selectedProduct}
				/>
			)}
			{isDeleteModalOpen &&
				selectedProduct &&
				createPortal(
					<DeleteModal
						isOpen={isDeleteModalOpen}
						onClose={handleCloseDeleteModal}
						onDelete={confirmDelete}
						text={`Вы уверены, что хотите удалить деталь ${selectedProduct.name || ''}?`}
						title="Удаление резиновой детали"
					/>,
					document.body,
				)}
		</Layout>
	);
};
